import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

// The product's dark stage (cooperly_app resources/js/features/stage/stage.tsx), the
// header panel of Pulse, Fundamentals and Random Coffee. Class strings follow the
// product so the preview reads as the same page; keep them in step with it. The
// preview is narrower than the product page, so callers may shrink the column
// bases to keep the chart beside the text.

export function Stage({
  labelledBy,
  aside,
  asideFirst = false,
  asideClassName,
  contentClassName,
  children,
  className,
}: {
  labelledBy: string;
  aside?: ReactNode;
  /** Put the visual above the text whenever the two stack. */
  asideFirst?: boolean;
  asideClassName?: string;
  /** Overrides the text column's share of the row. */
  contentClassName?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      aria-labelledby={labelledBy}
      className={cn(
        "relative isolate overflow-hidden rounded-[20px] border border-white/5 px-5 pt-7 pb-6 text-white sm:px-8 sm:pt-9 lg:pt-10 lg:pr-10 lg:pb-[34px] lg:pl-12",
        "bg-[radial-gradient(ellipse_52%_90%_at_78%_50%,#24242a_0%,#151518_52%,#0e0e10_100%)] selection:bg-white selection:text-zinc-950",
        className,
      )}
    >
      {/* Reverse wrapping keeps the visual on the right side by side and puts it on top once the row wraps. */}
      <div
        className={cn(
          "flex items-center gap-x-8 gap-y-10",
          asideFirst ? "flex-wrap-reverse justify-center" : "flex-wrap",
        )}
      >
        <div
          className={cn(
            "@container flex min-w-0 flex-[1_1_400px] flex-col items-start gap-[22px]",
            contentClassName,
          )}
        >
          {children}
        </div>
        {aside ? (
          <div
            className={cn(
              "flex min-w-0 flex-[0_1_520px] flex-col max-lg:basis-full",
              asideClassName,
            )}
          >
            {aside}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** Two-level heading: the answer in large white type and what it means in grey. */
export function StageHeadline({
  id,
  value,
  caption,
}: {
  id: string;
  value: ReactNode;
  caption?: ReactNode;
}) {
  return (
    <h2 id={id} className="m-0 flex max-w-full flex-col gap-1.5 sm:gap-2">
      <span className="text-[min(64px,14cqi)] leading-[0.95] font-semibold tracking-[-0.04em] break-words hyphens-auto">
        {value}
      </span>
      {caption ? (
        <>
          {" "}
          <span className="text-[22px] leading-[1.15] font-medium tracking-[-0.025em] text-white/60 sm:text-[28px]">
            {caption}
          </span>
        </>
      ) : null}
    </h2>
  );
}

export function StageLead({ children }: { children: ReactNode }) {
  return (
    <p className="m-0 max-w-[46ch] text-base leading-[1.55] text-pretty text-white/90 sm:text-[17px]">
      {children}
    </p>
  );
}

export type StageFact = {
  key: string;
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
};

/**
 * The facts strip between two hairlines; text facts are smaller and always one row.
 * Number facts go to one row by the text column's width rather than the viewport,
 * because the preview's column is narrower than the product page's.
 */
export function StageFacts({
  items,
  kind = "number",
}: {
  items: StageFact[];
  kind?: "number" | "text";
}) {
  const text = kind === "text";

  return (
    <dl
      className={cn(
        "m-0 grid w-full border-y border-white/12 py-3.5",
        text
          ? "max-w-[640px] auto-cols-[minmax(0,max-content)] grid-flow-col gap-x-0"
          : "max-w-[600px] grid-cols-2 gap-x-4 gap-y-4 @min-[22rem]:auto-cols-[minmax(0,max-content)] @min-[22rem]:grid-flow-col @min-[22rem]:grid-cols-none @min-[22rem]:gap-x-0",
      )}
    >
      {items.map((item) => (
        <div
          key={item.key}
          className={cn(
            "min-w-0",
            text
              ? "border-l border-white/12 px-3 first:border-l-0 first:pl-0 last:pr-0 sm:px-5"
              : "@min-[22rem]:border-l @min-[22rem]:border-white/12 @min-[22rem]:px-4 @min-[22rem]:first:border-l-0 @min-[22rem]:first:pl-0 @min-[22rem]:last:pr-0",
          )}
        >
          <dt className="text-xs leading-snug text-white/55">{item.label}</dt>
          <dd
            className={cn(
              "m-0 mt-[3px] break-words hyphens-auto",
              text
                ? "text-sm leading-[1.35] font-medium sm:text-[15px]"
                : "text-[22px] leading-[1.15] font-semibold tracking-[-0.02em]",
            )}
          >
            {item.value}
          </dd>
          {item.detail ? (
            <dd className="m-0 mt-0.5 text-xs text-white/60">{item.detail}</dd>
          ) : null}
        </div>
      ))}
    </dl>
  );
}

/** The quiet schedule line at the bottom of the stage; parts wrap between dots. */
export function StageMeta({ items }: { items: ReactNode[] }) {
  return (
    <p className="m-0 flex flex-wrap gap-x-1.5 text-[13px] leading-normal text-white/65">
      {items.map((item, index) => (
        <span key={index} className="whitespace-nowrap">
          {item}
          {index < items.length - 1 ? (
            <span aria-hidden="true" className="ml-1.5">
              ·
            </span>
          ) : null}
        </span>
      ))}
    </p>
  );
}

/** Chart title and colour key above a stage chart. */
export function StageChartHeading({
  title,
  titleId,
  legend,
  swatch,
}: {
  title: string;
  titleId: string;
  legend: Array<{ label: string; className: string }>;
  swatch: "square" | "dot";
}) {
  return (
    <div className="flex min-h-9 flex-wrap items-center gap-x-4 gap-y-1.5">
      <span id={titleId} className="text-[13px] text-white/75">
        {title}
      </span>
      <ul className="m-0 flex list-none flex-wrap gap-x-3.5 gap-y-1 p-0 text-xs text-white/65">
        {legend.map((item) => (
          <li key={item.label} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className={cn(
                swatch === "square" ? "size-2.5 rounded-[3px]" : "size-2 rounded-full",
                item.className,
              )}
            />
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
